/**
 * Order issuing (R21).
 *
 * **Every order becomes a command through the relay. There is no other path.**
 * That is what makes R4 structurally true rather than merely intended: this
 * file cannot mutate the simulation even if it wanted to.
 *
 * Order acknowledgement is client-side. Commands do not execute for 3 ticks
 * (100 ms), so the marker and cue fire on input rather than on execution —
 * otherwise the player clicks, nothing happens for a fifth of a second, and the
 * game reads as dropped input.
 */

import { Vector3 } from 'three'
import type { Ctx, Subsystem } from '../core/registry.js'
import { Entities, EF } from '../sim/entities.js'
import { Renderer } from '../render/index.js'
import { Selection } from './select.js'
import { CMD, type Command, type Rejection } from '../net/commands.js'
import type { Relay } from '../net/relay.js'
import * as fix from '../core/fixed.js'
import { World } from '../sim/world.js'
import { MAP_SIZE } from '../core/config.js'
import { ROLE, TAB, defById } from '../sim/data/entities.js'
import { Build } from '../sim/build.js'


/** A transient client-side marker drawn where an order was issued. */
export interface OrderMarker {
  x: number
  z: number
  /**
   * `rally` is not an order but a standing setting, redrawn each frame while
   * the building that owns it is selected. The others are acknowledgements and
   * fade.
   */
  kind: 'move' | 'attack-move' | 'attack' | 'rally'
  /** Frames remaining. Client state; never hashed. */
  life: number
}

const MARKER_LIFE = 30

export class Orders implements Subsystem {
  static id = 'orders'
  static deps = ['entities', 'world', 'render', 'selection'] as const

  private ctx!: Ctx
  private entities!: Entities
  private world!: World
  private renderer!: Renderer
  private selection!: Selection
  private build!: Build

  /** Set by the composition root — the only write path out of input. */
  relay!: Relay

  readonly markers: OrderMarker[] = []

  init(ctx: Ctx): void {
    this.ctx = ctx
    this.entities = ctx.get<Entities>('entities')
    this.world = ctx.get<World>('world')
    this.renderer = ctx.get<Renderer>('render')
    this.selection = ctx.get<Selection>('selection')
    this.build = ctx.get<Build>('build')
  }

  update(): void {
    for (let i = this.markers.length - 1; i >= 0; i--) {
      if (--this.markers[i].life <= 0) this.markers.splice(i, 1)
    }
    this.showRallyPoints()
    this.tickPendingLoad()
  }

  /**
   * A standing marker on the rally point of every selected producer.
   *
   * Rally points have worked since they were added and nothing drew them, so
   * the feature was reported missing twice: a player right-clicks, the order
   * is accepted, and the screen does not change. An order marker that fades
   * after a second is right for a move and wrong for this — a rally point is a
   * setting, not an event, so it is shown for as long as the building that
   * owns it is selected.
   */
  private showRallyPoints(): void {
    // Rebuilt each frame rather than diffed. The list is at most a handful of
    // entries and selection changes constantly.
    for (let i = this.markers.length - 1; i >= 0; i--) {
      if (this.markers[i].kind === 'rally') this.markers.splice(i, 1)
    }
    for (const id of this.selectedProducers()) {
      const slot = this.entities.resolve(id)
      if (slot < 0) continue
      const tab = defById(this.entities.kind[slot])?.producesTab
      if (tab === undefined) continue
      const cell = this.build.rallyFor(this.entities.owner[slot], tab)
      if (cell < 0) continue
      this.markers.push({
        x: (cell % MAP_SIZE) + 0.5,
        z: ((cell / MAP_SIZE) | 0) + 0.5,
        kind: 'rally',
        life: 2,
      })
    }
  }

  /**
   * The default order for a right-click at a screen point.
   *
   * U21 resolves ground-move and attack only. U14 extends this to the full
   * contextual set — unload at a refinery, mine regolith — resolved **per
   * selected entity against its own capability set**, with move as the
   * universal fallback.
   */
  /**
   * @param queue append a leg to the existing route instead of replacing it.
   */
  rightClick(clientX: number, clientY: number, queue = false): Rejection | null {
    this.selection.prune()
    if (this.selection.count === 0) return null

    // Ambiguity resolves by a fixed priority order, documented here so it is a
    // decision rather than an accident:
    //   enemy under the cursor  →  attack
    //   own refinery, loaded harvester selected  →  unload
    //   regolith cell, harvester selected  →  mine
    //   anything else  →  move
    //
    // Resolution runs **per selected entity against its own capability set**,
    // with move as the universal fallback. The mixed selection is the common
    // case — harvesters and tanks together right-clicking regolith — and a
    // per-selection rule produces a visibly different game from a per-unit one.
    const enemy = this.pickEnemy(clientX, clientY)
    if (enemy !== 0) return this.issueAttack(enemy)

    const world = this.renderer.screenToWorld(clientX, clientY, this.scratch)
    if (!world) return null

    const cx = Math.floor(world.x)
    const cy = Math.floor(world.z)
    if (!this.world.inBounds(cx, cy)) return null
    const cell = cy * MAP_SIZE + cx

    // A production building in the selection turns a right-click into a rally
    // point, exactly as it does in Red Alert: everything it builds from now on
    // walks there instead of standing in the doorway.
    //
    // Checked before the movement rules below, and it takes the whole click:
    // structures ignore move orders anyway, so there is nothing to share it
    // with, and a mixed selection of a factory and some tanks is far more
    // likely to be a stray click on the factory than a deliberate attempt to
    // do both at once.
    const producers = this.selectedProducers()
    if (producers.length > 0) {
      let rejection: Rejection | null = null
      for (const id of producers) {
        rejection =
          this.relay.submit({ t: CMD.Rally, structure: id, cx, cy } as Omit<
            Command,
            'slot' | 'seq'
          >) ?? rejection
      }
      return rejection
    }

    const friendly = this.selection.pick(clientX, clientY, true)
    const friendlySlot = this.entities.resolve(friendly)

    // An empty transport under the cursor with infantry selected: load it.
    // Checked before the movement rules, because right-clicking a transport
    // with a squad selected has no other sensible reading.
    if (
      friendlySlot >= 0 &&
      this.entities.owner[friendlySlot] === this.selection.localSlot &&
      defById(this.entities.kind[friendlySlot])?.carriesInfantry === true &&
      this.entities.passenger[friendlySlot] === 0
    ) {
      const rider = this.pickRider()
      if (rider !== 0) return this.loadInto(rider, friendly, friendlySlot)
    }

    // A damaged friendly structure under the cursor with an engineer selected:
    // walk the engineer over and repair it.
    if (
      friendlySlot >= 0 &&
      this.entities.owner[friendlySlot] === this.selection.localSlot &&
      this.entities.has(friendlySlot, EF.Structure) &&
      this.entities.hp[friendlySlot] < this.entities.maxHp[friendlySlot]
    ) {
      const engineer = this.pickEngineer()
      if (engineer !== 0) return this.mendWith(engineer, friendly, friendlySlot)
    }
    const isOwnRefinery =
      friendlySlot >= 0 &&
      this.entities.owner[friendlySlot] === this.selection.localSlot &&
      defById(this.entities.kind[friendlySlot])?.role === ROLE.Refinery

    const harvesters: number[] = []
    const others: number[] = []
    for (const id of this.selection.selected) {
      const slot = this.entities.resolve(id)
      if (slot < 0) continue
      // A structure in the selection ignores movement orders entirely rather
      // than rejecting the whole command.
      if (!this.entities.has(slot, EF.Mobile)) continue
      if (this.entities.has(slot, EF.Harvester)) harvesters.push(id)
      else others.push(id)
    }

    let rejection: Rejection | null = null
    if (harvesters.length > 0) {
      if (isOwnRefinery) {
        rejection = this.submitUnits(CMD.Unload, harvesters, friendly) ?? rejection
      } else if (this.world.regolithAt(cx, cy) > 0) {
        rejection = this.submitUnits(CMD.Mine, harvesters, cell) ?? rejection
      } else if (queue) {
        rejection = this.submitMove(CMD.Waypoint, harvesters, world.x, world.z) ?? rejection
      } else {
        rejection = this.issueMoveFor(harvesters, world.x, world.z, false) ?? rejection
      }
    }
    if (others.length > 0) {
      rejection = queue
        ? this.submitMove(CMD.Waypoint, others, world.x, world.z)
        : (this.issueMoveFor(others, world.x, world.z, false) ?? rejection)
    }
    if (harvesters.length === 0 && others.length === 0) return null
    return rejection
  }

  private submitUnits(t: number, units: number[], target: number): Rejection | null {
    const rejection = this.relay.submit({
      t,
      units: Uint32Array.from(units),
      target,
    } as unknown as Omit<Command, 'slot' | 'seq'>)
    if (!rejection) {
      this.ctx.emit('order:acknowledged', { kind: 'harvest', count: units.length })
    }
    return rejection
  }

  /** Attack-move to a screen point. */
  attackMove(clientX: number, clientY: number): Rejection | null {
    this.selection.prune()
    if (this.selection.count === 0) return null
    const world = this.renderer.screenToWorld(clientX, clientY, this.scratch)
    if (!world) return null
    return this.issueMove(world.x, world.z, true)
  }

  issueMove(worldX: number, worldZ: number, attackMove: boolean): Rejection | null {
    return this.issueMoveFor(this.movableSelection(), worldX, worldZ, attackMove)
  }

  private issueMoveFor(
    units: number[],
    worldX: number,
    worldZ: number,
    attackMove: boolean,
  ): Rejection | null {
    if (units.length === 0) return null

    const cx = Math.floor(worldX)
    const cy = Math.floor(worldZ)
    if (!this.world.inBounds(cx, cy)) return null

    const rejection = this.relay.submit({
      t: attackMove ? CMD.AttackMove : CMD.Move,
      units: Uint32Array.from(units),
      x: fix.fromFloat(worldX),
      y: fix.fromFloat(worldZ),
    } as Omit<Command, 'slot' | 'seq'>)

    // Acknowledge immediately, on input, not on execution.
    if (!rejection) {
      this.markers.push({
        x: worldX,
        z: worldZ,
        kind: attackMove ? 'attack-move' : 'move',
        life: MARKER_LIFE,
      })
      this.ctx.emit('order:acknowledged', { kind: attackMove ? 'attack-move' : 'move', count: units.length })
    } else {
      this.ctx.emit('order:rejected', { reason: rejection.reason })
    }
    return rejection
  }

  issueAttack(targetId: number): Rejection | null {
    const units = this.movableSelection()
    if (units.length === 0) return null
    const slot = this.entities.resolve(targetId)
    if (slot < 0) return null

    const rejection = this.relay.submit({
      t: CMD.Attack,
      units: Uint32Array.from(units),
      target: targetId,
    } as Omit<Command, 'slot' | 'seq'>)

    if (!rejection) {
      this.markers.push({
        x: fix.toFloat(fix.raw(this.entities.x[slot])),
        z: fix.toFloat(fix.raw(this.entities.y[slot])),
        kind: 'attack',
        life: MARKER_LIFE,
      })
      this.ctx.emit('order:acknowledged', { kind: 'attack', count: units.length })
    } else {
      this.ctx.emit('order:rejected', { reason: rejection.reason })
    }
    return rejection
  }

  issueStop(): Rejection | null {
    const units = this.movableSelection()
    if (units.length === 0) return null
    return this.relay.submit({
      t: CMD.Stop,
      units: Uint32Array.from(units),
    } as Omit<Command, 'slot' | 'seq'>)
  }

  /**
   * Deploy every selected founder unit (R11).
   *
   * Bound to D, and to a double-click on the unit itself. F1's opening move is
   * deploying the MCV, so a game where that move has no binding is a game a
   * human cannot start.
   */
  /**
   * Repair or sell every selected structure.
   *
   * Both are deliberately restricted to what is selected rather than being a
   * cursor mode you click buildings with. A modal sell cursor is the classic
   * way to lose a Construction Yard to a misclick, and selection already gives
   * an unambiguous answer to "which building did you mean".
   */
  /** One move-shaped command for a set of units. */
  private submitMove(
    t: typeof CMD.Waypoint | typeof CMD.Move,
    units: number[],
    worldX: number,
    worldZ: number,
  ): Rejection | null {
    return (
      this.relay.submit({
        t,
        units: Uint32Array.from(units),
        x: fix.fromFloat(worldX),
        y: fix.fromFloat(worldZ),
      } as Omit<Command, 'slot' | 'seq'>) ?? null
    )
  }

  /** The first infantryman in the selection, or 0. */
  private pickRider(): number {
    for (const id of this.selection.selected) {
      const slot = this.entities.resolve(id)
      if (slot < 0) continue
      const def = defById(this.entities.kind[slot])
      if (!def || def.isStructure || def.tab !== TAB.Infantry) continue
      return id
    }
    return 0
  }

  /** The first engineer in the selection, or 0. */
  private pickEngineer(): number {
    for (const id of this.selection.selected) {
      const slot = this.entities.resolve(id)
      if (slot < 0) continue
      if (defById(this.entities.kind[slot])?.isEngineer === true) return id
    }
    return 0
  }

  /**
   * Walk an infantryman to a transport, and board it when it arrives.
   *
   * The order is issued twice on purpose: a move now, and the load itself once
   * the two are adjacent. The alternative — a load order the simulation holds
   * on to until the walk finishes — would mean the build subsystem owning a
   * pending movement, with its own state to keep and its own way to be
   * cancelled. This way the walk is an ordinary move that the player can
   * override by giving any other order.
   */
  private loadInto(rider: number, transport: number, transportSlot: number): Rejection | null {
    const riderSlot = this.entities.resolve(rider)
    if (riderSlot < 0) return null
    if (this.withinReach(riderSlot, transportSlot)) {
      this.pendingApproach = null
      return this.relay.submit({ t: CMD.Load, unit: rider, transport } as Omit<
        Command,
        'slot' | 'seq'
      >)
    }
    this.pendingApproach = { t: CMD.Load, unit: rider, target: transport }
    const tx = this.entities.x[transportSlot] >> 16
    const ty = this.entities.y[transportSlot] >> 16
    return this.submitMove(CMD.Move, [rider], tx + 0.5, ty + 0.5)
  }

  /**
   * Walk an engineer to a damaged structure and repair it on arrival. The
   * same two-step shape as a load, and for the same reason: the walk is an
   * ordinary move the player can override with any other order.
   */
  private mendWith(engineer: number, structure: number, structureSlot: number): Rejection | null {
    const engineerSlot = this.entities.resolve(engineer)
    if (engineerSlot < 0) return null
    if (this.withinReach(engineerSlot, structureSlot)) {
      this.pendingApproach = null
      return this.relay.submit({ t: CMD.Mend, unit: engineer, structure } as Omit<
        Command,
        'slot' | 'seq'
      >)
    }
    this.pendingApproach = { t: CMD.Mend, unit: engineer, target: structure }
    const tx = this.entities.x[structureSlot] >> 16
    const ty = this.entities.y[structureSlot] >> 16
    return this.submitMove(CMD.Move, [engineer], tx + 0.5, ty + 0.5)
  }

  /**
   * Within one cell of the target's footprint. A structure is measured from
   * its whole footprint, not its centre — a 3×3 building's centre is out of
   * reach from every cell a unit can stand in.
   */
  private withinReach(unitSlot: number, targetSlot: number): boolean {
    const def = defById(this.entities.kind[targetSlot])
    const w = def?.width ?? 1
    const h = def?.height ?? 1
    const tx = (this.entities.x[targetSlot] >> 16) - (w >> 1)
    const ty = (this.entities.y[targetSlot] >> 16) - (h >> 1)
    const ux = this.entities.x[unitSlot] >> 16
    const uy = this.entities.y[unitSlot] >> 16
    const dx = ux < tx ? tx - ux : ux - (tx + w - 1)
    const dy = uy < ty ? ty - uy : uy - (ty + h - 1)
    return Math.max(dx, dy) <= 1
  }

  /** An action waiting on its unit to finish walking to the target. */
  private pendingApproach: {
    t: typeof CMD.Load | typeof CMD.Mend
    unit: number
    target: number
  } | null = null

  /**
   * Finish a pending load or mend once the walker has arrived.
   *
   * Polled rather than event-driven because arrival is not an event the
   * pathfinder reports, and a walk can also simply fail — the unit is blocked,
   * or the transport drove off. Re-checking each frame handles all of those
   * the same way, and the order expires the moment anything else is ordered.
   */
  private tickPendingLoad(): void {
    const pending = this.pendingApproach
    if (!pending) return
    const unitSlot = this.entities.resolve(pending.unit)
    const targetSlot = this.entities.resolve(pending.target)
    if (unitSlot < 0 || targetSlot < 0) {
      this.pendingApproach = null
      return
    }
    // The reason to arrive can disappear en route: the transport picked
    // somebody else up, or the structure was repaired the ordinary way.
    const done =
      pending.t === CMD.Load
        ? this.entities.passenger[targetSlot] !== 0
        : this.entities.hp[targetSlot] >= this.entities.maxHp[targetSlot]
    if (done) {
      this.pendingApproach = null
      return
    }
    if (!this.withinReach(unitSlot, targetSlot)) return
    this.pendingApproach = null
    if (pending.t === CMD.Load) {
      this.relay.submit({ t: CMD.Load, unit: pending.unit, transport: pending.target } as Omit<
        Command,
        'slot' | 'seq'
      >)
    } else {
      this.relay.submit({ t: CMD.Mend, unit: pending.unit, structure: pending.target } as Omit<
        Command,
        'slot' | 'seq'
      >)
    }
  }

  private selectedProducers(): number[] {
    const out: number[] = []
    for (const id of this.selection.selected) {
      const slot = this.entities.resolve(id)
      if (slot < 0) continue
      if (!this.entities.has(slot, EF.Structure)) continue
      const def = defById(this.entities.kind[slot])
      // Asks the data whether it builds anything, rather than keeping a list
      // of roles that would need updating for every new production building.
      if (def?.producesTab === undefined) continue
      out.push(id)
    }
    return out
  }

  /**
   * Nominate the structure under the cursor as the one to build from.
   *
   * Double-click, which is where Red Alert puts it. With one factory it does
   * nothing; with two it is the difference between knowing where your tanks
   * will appear and finding out.
   *
   * Returns whether the click was *claimed*, not whether the order succeeded.
   * The caller needs to know if it should still start a selection drag, and
   * "no rejection" is the wrong signal for that — an accepted order and a
   * double-click on empty ground both produce no rejection, so keying on it
   * makes every double-click anywhere swallow the selection.
   */
  setPrimary(clientX: number, clientY: number): boolean {
    const id = this.selection.pick(clientX, clientY, true)
    const slot = this.entities.resolve(id)
    if (slot < 0) return false
    if (this.entities.owner[slot] !== this.selection.localSlot) return false
    if (defById(this.entities.kind[slot])?.producesTab === undefined) return false
    this.relay.submit({ t: CMD.Primary, structure: id } as Omit<Command, 'slot' | 'seq'>)
    this.ctx.emit('order:acknowledged', { kind: 'primary', count: 1 })
    return true
  }

  issueRepair(): Rejection | null {
    return this.forEachStructure(CMD.Repair, 'nothing selected can be repaired')
  }

  issueSell(): Rejection | null {
    return this.forEachStructure(CMD.Sell, 'nothing selected can be sold')
  }

  private forEachStructure(t: typeof CMD.Repair | typeof CMD.Sell, empty: string): Rejection | null {
    this.selection.prune()
    let last: Rejection | null = null
    let any = false
    for (const id of this.selection.selected) {
      const slot = this.entities.resolve(id)
      if (slot < 0) continue
      if (!this.entities.has(slot, EF.Structure)) continue
      any = true
      last = this.relay.submit({ t, structure: id } as Omit<Command, 'slot' | 'seq'>) ?? last
    }
    if (!any) {
      this.ctx.emit('order:rejected', {
        owner: this.selection.localSlot,
        reason: 'malformed',
        detail: empty,
      })
    }
    return last
  }

  /**
   * D: deploy a founder into a building, or dig infantry in where it stands.
   *
   * One key, two meanings, decided per unit — both are "commit to this spot".
   * The filter here has to know about both: it was written when a founder was
   * the only thing that could deploy, and left as it was it refuses the order
   * for infantry before the simulation ever sees it, with a message about
   * founding a base that has nothing to do with what the player asked for.
   */
  issueDeploy(): Rejection | null {
    this.selection.prune()
    let last: Rejection | null = null
    let any = false
    for (const id of this.selection.selected) {
      const slot = this.entities.resolve(id)
      if (slot < 0) continue
      const def = defById(this.entities.kind[slot])
      const canFound = this.entities.has(slot, EF.Founder)
      const canDig = def?.canDigIn === true
      // A loaded transport deploys too: D puts the passenger down. The filter
      // predates transports, and without this line the simulation's unload
      // path was unreachable from the key that is supposed to trigger it.
      const canUnload = def?.carriesInfantry === true && this.entities.passenger[slot] !== 0
      if (!canFound && !canDig && !canUnload) continue
      any = true
      last = this.relay.submit({ t: CMD.Deploy, unit: id } as Omit<Command, 'slot' | 'seq'>) ?? last
    }
    if (!any) {
      this.ctx.emit('order:rejected', {
        owner: this.selection.localSlot,
        reason: 'malformed',
        detail: 'nothing selected can deploy, dig in, or unload',
      })
    }
    return last
  }

  issueGuard(): Rejection | null {
    const units = this.movableSelection()
    if (units.length === 0) return null
    return this.relay.submit({
      t: CMD.Guard,
      units: Uint32Array.from(units),
    } as Omit<Command, 'slot' | 'seq'>)
  }

  issueScatter(): Rejection | null {
    const units = this.movableSelection()
    if (units.length === 0) return null
    return this.relay.submit({
      t: CMD.Scatter,
      units: Uint32Array.from(units),
    } as Omit<Command, 'slot' | 'seq'>)
  }

  /**
   * Selected units that can actually take a movement order. A structure in the
   * selection ignores movement orders rather than rejecting the whole command —
   * mixed selections are the common case.
   */
  private movableSelection(): number[] {
    const out: number[] = []
    for (const id of this.selection.selected) {
      const slot = this.entities.resolve(id)
      if (slot < 0) continue
      if (!this.entities.has(slot, EF.Mobile)) continue
      out.push(id)
    }
    return out
  }

  private pickEnemy(clientX: number, clientY: number): number {
    const id = this.selection.pick(clientX, clientY, true)
    if (id === 0) return 0
    const slot = this.entities.resolve(id)
    if (slot < 0) return 0
    return this.entities.owner[slot] === this.selection.localSlot ? 0 : id
  }

  private readonly scratch = new Vector3()
}
